import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LogIn, ShieldCheck, ShieldOff, Search, Store, RefreshCcw } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";

export default function TenantsManagement() {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState("");
  const [impersonating, setImpersonating] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users?role=manager");
      setManagers(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const filtered = managers.filter((m) => {
    const q = keyword.trim().toLowerCase();
    if (!q) return true;
    return (
      (m.name || "").toLowerCase().includes(q) ||
      (m.login_id || "").toLowerCase().includes(q) ||
      (m.tenant_slug || "").toLowerCase().includes(q)
    );
  });

  const impersonate = async (mgr) => {
    setImpersonating(mgr.id);
    try {
      const { data } = await api.post(`/admin-access/impersonate/${mgr.id}`);
      // 別タブで開く: token を URL hash で渡す
      const url = new URL(window.location.origin + "/manager");
      // sessionStorage を一時的に書き換える方が安全
      // 新しいタブで開く前に一時保管できるよう、token を別キーで保存
      const stash = {
        token: data.access_token,
        user: data.user,
        impersonated_by: data.impersonated_by,
        ts: Date.now(),
      };
      sessionStorage.setItem("impersonate_payload", JSON.stringify(stash));
      // 同タブで遷移するとadmin tokenが上書きされてしまうため、必ず別ウィンドウ
      const w = window.open(url.toString() + "?impersonate=1", "_blank");
      if (!w) {
        toast.error("ポップアップがブロックされました。許可してから再度お試しください");
      } else {
        toast.success(`${mgr.name} として代理ログインします（別タブ）`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImpersonating(null);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4" data-testid="tenants-page">
      <header className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <Store className="w-6 h-6 text-blue-600" />
            テナント管理 / 代理ログイン
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            運営アクセス許可がONの店舗にのみ、代理ログインできます。
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="reload-tenants">
          <RefreshCcw className="w-3.5 h-3.5 mr-1" />更新
        </Button>
      </header>

      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-slate-400" />
          <Input
            placeholder="店舗名・ログインID・slug で検索"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="flex-1 max-w-sm"
            data-testid="tenant-search"
          />
        </div>

        {loading ? (
          <p className="text-sm text-slate-400 py-8 text-center">読み込み中...</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">該当する店舗がありません</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">店舗名 / 店長</th>
                  <th className="px-3 py-2 text-left">ログインID</th>
                  <th className="px-3 py-2 text-left">slug</th>
                  <th className="px-3 py-2 text-left">運営アクセス</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((m) => {
                  const allowed = !!m.admin_access_allowed;
                  return (
                    <tr key={m.id} className="border-t border-slate-100" data-testid={`tenant-row-${m.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold">{m.name}</div>
                        <div className="text-[10px] text-slate-400">
                          {m.is_active === false ? "非アクティブ" : "アクティブ"}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{m.login_id}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-600">{m.tenant_slug || "—"}</td>
                      <td className="px-3 py-2">
                        {allowed ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 text-xs font-bold">
                            <ShieldCheck className="w-3 h-3" />許可中
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 text-xs font-bold">
                            <ShieldOff className="w-3 h-3" />ブロック
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="sm"
                          disabled={!allowed || impersonating === m.id}
                          onClick={() => impersonate(m)}
                          className={`h-7 text-xs ${allowed ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-200 text-slate-400 cursor-not-allowed"}`}
                          data-testid={`impersonate-${m.id}`}
                          title={allowed ? "別タブで代理ログイン" : "運営アクセスが許可されていません"}
                        >
                          <LogIn className="w-3 h-3 mr-1" />
                          {impersonating === m.id ? "..." : "代理ログイン"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-4 bg-amber-50 border border-amber-200">
        <p className="text-xs text-amber-900 leading-relaxed">
          <b>注意：</b>代理ログインはすべて記録され、対象店舗の管理画面の「運営アクセス履歴」に表示されます。
          サポート以外の目的では使用しないでください。
        </p>
      </Card>
    </div>
  );
}
